import { useAuth0 } from '@auth0/auth0-react';
import { LogoutWarningDialog } from '@strata/react-dialog';
import { BlankScreen } from '@strata/react-page-templates';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router';

import { Header } from 'components/header/Header';
import { clearDataForLogout, getFMHTokenAndEnsureSocketConnections } from 'services/authHelpers';
import { LOGIN_REDIRECT_KEY, type Permissions } from 'services/constants';
import { usePayerFeatures } from 'services/FeatureFlags';
import { updatePendoUserMetadata } from 'services/pendoUtils';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { useGetPayerDetailsQuery, useGetSegmentsQuery } from 'store/api/referenceApi';

const TOKEN_REFRESH_THRESHOLD = 5; // minutes

/** Routes that header is hidden on */
const NoHeaderRoutes: string[] = [
    AppRoutes.termsOfUse,
    AppRoutes.privacyPolicy,
    AppRoutes.updatesHistory,
];

interface RequireAuthProps {
    requiredPayerFeature?: string;
    permissionsToCheck?: (typeof Permissions)[keyof typeof Permissions][];
    needAllPermissions?: boolean;
}

/**
 * Component to determine if a user is authenticated and
 * if they are allowed to visit the page they navigated to.
 *
 * If they are: they proceed to the page
 * If not: they are redirected to the login page.
 *
 * This component will also refresh the access token if the user is authenticated.
 */
export const RequireAuth = ({
    requiredPayerFeature = undefined,
    permissionsToCheck = undefined,
    needAllPermissions = false,
}: RequireAuthProps) => {
    const { isAuthenticated, loginWithRedirect, getAccessTokenSilently, logout, user } = useAuth0();
    const location = useLocation();
    const dispatch = useDispatch();
    const { payerUserLogoutEnhancement } = useFlags();
    const [tokenFetchedAt, setTokenFetchedAt] = useState(dayjs());

    const getAccessTokens = useCallback(async () => {
        await getAccessTokenSilently({
            cacheMode: 'off', // Prevents retrieving from cache
        });

        await getFMHTokenAndEnsureSocketConnections({
            getAccessTokenSilently,
            logout,
            dispatch,
        });
        setTokenFetchedAt(dayjs());
    }, [getAccessTokenSilently, logout, dispatch]);

    const wrapGetAccessTokenSilently = useCallback(async () => {
        try {
            // Call Auth0 API to get access token, which will refresh the session and cache the token
            await getAccessTokens();
        } catch (e) {
            // An error in getting the access token invalidates the session and will require logging in again.
            // eslint-disable-next-line no-console
            console.error('Error getting access token silently', e);
        }
    }, [getAccessTokens]);

    useEffect(() => {
        // If authenticated, check if user is in the store and whether to refresh the token
        void (async () => {
            if (
                isAuthenticated &&
                !!user &&
                dayjs().diff(tokenFetchedAt, 'minutes') >= TOKEN_REFRESH_THRESHOLD
            ) {
                await wrapGetAccessTokenSilently();
            } else if (!isAuthenticated) {
                sessionStorage.setItem(
                    LOGIN_REDIRECT_KEY,
                    `${location.pathname}${location.search}${location.hash}`,
                );
                await loginWithRedirect();
                // AuthCallback will handle the redirect
            }
        })();
    }, [
        isAuthenticated,
        location,
        loginWithRedirect,
        tokenFetchedAt,
        user,
        wrapGetAccessTokenSilently,
    ]);

    // Ensure permissions, profile, and payer details are loaded
    const { payerId, permissions, segmentIds } = useAppSelector((state) => state.auth);
    const payerFeatures = usePayerFeatures();
    const userDataLoaded = !!payerFeatures;

    // Set Pendo metadata
    const { data: payerDetails } = useGetPayerDetailsQuery(
        { payerId: payerId! },
        { skip: !isAuthenticated },
    );
    const { data: segments } = useGetSegmentsQuery(
        { payerId: payerId! },
        { skip: !isAuthenticated },
    );
    useEffect(() => {
        if (payerDetails?.payerName && segments?.segmentsMap) {
            const segmentNames = segmentIds.map((segmentId) => segments.segmentsMap[segmentId]);
            updatePendoUserMetadata(payerDetails.payerName, segmentNames);
        }
    }, [payerDetails, segments, segmentIds]);

    if (!isAuthenticated) {
        return <BlankScreen />;
    }

    let hasPermissions = true;
    if (permissionsToCheck) {
        if (needAllPermissions) {
            hasPermissions = permissionsToCheck.every((permission) =>
                permissions.includes(permission),
            );
        } else {
            hasPermissions = permissionsToCheck.some((permission) =>
                permissions.includes(permission),
            );
        }
    }

    // Redirect to home if payer doesn't have required feature
    if (
        userDataLoaded &&
        (!!(requiredPayerFeature && !payerFeatures?.[requiredPayerFeature]) || !hasPermissions)
    ) {
        return <Navigate replace to={AppRoutes.home} />;
    }

    return (
        <>
            {!NoHeaderRoutes.includes(location.pathname) && <Header />}
            {payerUserLogoutEnhancement && (
                <LogoutWarningDialog
                    tokenFetchedAt={tokenFetchedAt.toISOString()}
                    onExtend={getAccessTokens}
                    onLogout={async (isUserInitiated: boolean) => {
                        clearDataForLogout(isUserInitiated);
                        await logout({
                            logoutParams: {
                                returnTo: `${window.location.origin}${AppRoutes.loggedOut}`,
                            },
                        });
                    }}
                />
            )}
            {!userDataLoaded ? <BlankScreen /> : <Outlet />}
        </>
    );
};
